import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import {
  GuestApiError,
  recordGuestCta,
  startGuestWhatsAppSignup,
  TodoListGuestChannel,
  TodoListGuestWhatsAppSignupResponse,
} from './guestApi';

interface GuestNudgeModalProps {
  open: boolean;
  onClose: () => void;
  token: string;
  /** How the guest was invited — decides the call to action. */
  channel: TodoListGuestChannel;
}

type SignupOutcome = NonNullable<
  TodoListGuestWhatsAppSignupResponse['outcome']
>;

/**
 * "Get your own PA" nudge shown after a guest ticks off their first task
 * (Figma 5249:4898). WhatsApp invitees get a button that opens a WhatsApp
 * chat pre-filled with the inviter's referral code; email invitees enter a
 * phone number and Dream's assistant messages them on WhatsApp instead.
 */
const GuestNudgeModal: React.FC<GuestNudgeModalProps> = ({
  open,
  onClose,
  token,
  channel,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [outcome, setOutcome] = useState<SignupOutcome | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const openWhatsApp = async () => {
    setBusy(true);
    setError(null);
    try {
      const { whatsappUrl } = await recordGuestCta(token);
      if (whatsappUrl) window.location.assign(whatsappUrl);
    } catch (err) {
      setError(
        err instanceof GuestApiError ? err.message : t('todoGuest.nudge.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await startGuestWhatsAppSignup(token, phone);
      setOutcome(result.outcome ?? 'sent');
    } catch (err) {
      setError(
        err instanceof GuestApiError && err.status === 400
          ? t('todoGuest.nudge.phoneInvalid')
          : t('todoGuest.nudge.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className='guest-nudge-cover'
      onClick={onClose}
      data-testid='guest-nudge'
    >
      <div
        className='guest-nudge'
        role='dialog'
        aria-modal='true'
        aria-labelledby='guest-nudge-title'
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id='guest-nudge-title' className='guest-nudge-title'>
          {t('todoGuest.nudge.title')}
        </h2>
        <p className='guest-nudge-body'>{t('todoGuest.nudge.body')}</p>

        {channel === 'phone' ? (
          <button
            type='button'
            className='guest-nudge-cta'
            onClick={openWhatsApp}
            disabled={busy}
          >
            <span>{t('todoGuest.nudge.getYourOwnPa')}</span>
            <ArrowRightIcon />
          </button>
        ) : outcome ? (
          <p className='guest-nudge-outcome' role='status'>
            {t(`todoGuest.nudge.outcome.${outcome}`)}
          </p>
        ) : (
          <form className='guest-nudge-phone' onSubmit={submitPhone}>
            <label htmlFor='guest-nudge-phone-input'>
              {t('todoGuest.nudge.phonePrompt')}
            </label>
            <div className='guest-nudge-phone-row'>
              <input
                id='guest-nudge-phone-input'
                type='tel'
                autoComplete='tel'
                inputMode='tel'
                placeholder={t('todoGuest.nudge.phonePlaceholder')}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy}
              />
              <button
                type='submit'
                aria-label={t('todoGuest.nudge.phoneSubmit')}
                disabled={busy || !phone.trim()}
              >
                <ArrowRightIcon />
              </button>
            </div>
          </form>
        )}

        {error && (
          <p className='guest-nudge-error' role='alert'>
            {error}
          </p>
        )}

        <button type='button' className='guest-nudge-later' onClick={onClose}>
          {t('todoGuest.nudge.later')}
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default GuestNudgeModal;
